import Vue from 'vue'
import axios from 'axios'
import { getToken } from './auth'
import qs from 'qs'
import { 
  encodeFields, 
  decodeFields, 
  encodeObject, 
  decodeObject, 
  encodeStringify,
  encodeFieldsByPath,
  decodeFieldsByPath
} from './requestCrypto'
import { triggerLogout } from './triggerParent'

// 创建axios实例
const request = axios.create({
  baseURL: '', // api 的 base_url
  timeout: 20000 // 请求超时时间
})

// 重复请求取消
const pending = []
const CancelToken = axios.CancelToken
const removePending = config => {
  for (const p in pending) {
    if (pending[p].u === config.url + JSON.stringify(config.data) + '&' + config.method) {
      pending[p].f()
      pending.splice(p, 1)
    }
  }
}

// 请求拦截器
request.interceptors.request.use(
  config => {
    removePending(config)
    config.cancelToken = new CancelToken(cancel => {
      pending.push({
        u: config.url + JSON.stringify(config.data) + '&' + config.method,
        f: cancel
      })
    })
    // localStorage 取不到cookie的情况下 使用cookie的
    const token = localStorage.getItem('token') || getToken()
    if (token) {
      config.headers.Authorization = token
    } else {
      // 没有token的情况下调外部退出方法
      // triggerLogout()
    }
    return config
  },
  error => {
    Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  response => {
    removePending(response.config)

    const _config = response.config
    let res = response.data

    // 从配置中解构解密相关参数
    const { resCryptoKeys, resultKey, cryptoMethod } = _config.config || {}
    // 如果配置了需要解密的字段，则对返回数据进行解密处理
    if (resCryptoKeys) {
      // 'all' 表示解密整个数据，否则只解密指定的字段
      res[resultKey] = resCryptoKeys === 'all' 
        ? decodeObject({ data: res[resultKey], cryptoMethod })
        : decodeFieldsByPath({ data: res[resultKey], keys: resCryptoKeys, cryptoMethod })
    }

    // 请求拦截器加一层自定义code拦截 customMsg判断自定义拦截
    const customMsg = _config.config && _config.config.customMsg ? _config.config.customMsg : false
    if (customMsg) return Promise.resolve(res)

    // 最终结果返回
    return res
  },
  error => {
    // console.log('err--' + error)
    if (error == 'Cancel' || error == 'CanceledError: canceled') {
      return
    }
    showRequestErrorMessage('接口请求异常，请稍后再试!')
    // Promise.reject(error)
  }
)

const showRequestErrorMessage = msg => {
  Vue.prototype.$message.closeAll()
  Vue.prototype.$message({
    message: msg,
    type: 'error',
    duration: 5000
  })
}

export const _request = {
  /**
   * GET 请求方法
   * @param {string} url - 请求地址
   * @param {object} data - 请求参数对象
   * @param {object} config - 配置选项
   * @param {string|string[]} config.reqCryptoKeys - 请求参数需要加密的字段名，'all'表示整体加密
   * @param {string|string[]} config.resCryptoKeys - 返回数据需要解密的字段名，'all'表示整体解密
   * @param {string} config.resultKey - 返回值中存放返回数据的key (result、data、body)
   * @param {string} config.cryptoMethod - 加密方法，默认使用 Base64
   * @returns {Promise} axios 请求 Promise
   */
  get: (url, data, config = {}) => {
    // 从配置中解构加密相关参数
    const { reqCryptoKeys, cryptoMethod } = config
    // 如果配置了需要加密的字段，则对请求参数进行加密处理
    if (reqCryptoKeys) {
      // 'all' 表示加密整个对象，否则只加密指定的字段
      data = reqCryptoKeys === 'all' 
        ? encodeStringify({ data, cryptoMethod }) // 加密整个对象
        : encodeFieldsByPath({ data, keys: reqCryptoKeys, cryptoMethod }) // 加密指定字段
    }

    return request({ method: 'get', url, params: data, config })
  },
  /**
   * POST 请求方法
   * @param {string} url - 请求地址
   * @param {object} data - 请求参数对象
   * @param {object} config - 配置选项
   * @param {string|string[]} config.reqCryptoKeys - 请求参数需要加密的字段名，'all'表示整体加密
   * @param {string|string[]} config.resCryptoKeys - 返回数据需要解密的字段名，'all'表示整体解密
   * @param {string} config.resultKey - 返回值中存放返回数据的key (result、data、body)
   * @param {string} config.cryptoMethod - 加密方法，默认使用 Base64
   * @returns {Promise} axios 请求 Promise
   */
  post: (url, data, config = {}) => {
    // 从配置中解构加密相关参数
    const { reqCryptoKeys, cryptoMethod } = config
    // 如果配置了需要加密的字段，则对请求参数进行加密处理
    if (reqCryptoKeys) {
      // 'all' 表示加密整个对象，否则只加密指定的字段
      data = reqCryptoKeys === 'all' 
        ? encodeObject({ data, cryptoMethod }) // 加密整个对象
        : encodeFieldsByPath({ data, keys: reqCryptoKeys, cryptoMethod }) // 加密指定字段
    }

    return request({
      method: 'post',
      url, 
      data: config.isFormData ? qs.stringify(data) : data, 
      config 
    })
  },
}

export default request